import { ReactElement, useEffect, useState } from 'react';
import { Heading } from '@appsumo/dorado-react';
import { useToggle } from 'usehooks-ts';
import { toast } from 'react-hot-toast';

import { CheckoutStepProps, SetStepErrorMessageType } from '~/types/checkout';
import { UserObjectProps } from '~/types/user';
import { STEP } from '~/constants/checkout';
import { EMAIL_ONLY_CHECKOUT_EXPERIMENT_NAME } from '~/lib/util/constants';
import { useExperiment } from '~/lib/experiment';
import { useCheckoutState, useCheckoutAPI } from '~/hooks/checkout';

import { CheckoutCard, Loading } from '../Global';
import SocialLogin from '~/components/checkout/Account/SocialLogin';
import SignUp from '~/components/checkout/Account/SignUp';
import SignUpEmailOnly from '~/components/checkout/Account/SignUpEmailOnly';
import SignIn from '~/components/checkout/Account/SignIn';

interface CreateAccountProps {
  isLoading: boolean;
  setStep: (step: CheckoutStepProps) => void;
  step: CheckoutStepProps;
  user?: UserObjectProps;
  setStepErrorMessage: SetStepErrorMessageType;
}

function CreateAccount({
  setStep,
  step,
  user,
  isLoading,
  setStepErrorMessage,
}: Readonly<CreateAccountProps>): ReactElement {
  const [showSignUp, setShowSignUp] = useToggle(true); // false: show sign in, true: show sign up/create account
  const { variant } = useExperiment(EMAIL_ONLY_CHECKOUT_EXPERIMENT_NAME);
  const [isSubmittingAccount, setIsSubmittingAccount] =
    useState<boolean>(false);

  if (step !== STEP.ACCOUNT || user || isLoading) return <></>;

  let accountText = 'Already have an account?';
  let headerText = 'Create an account';
  let buttonText = 'Sign in';
  let loginComponent =
    variant === 'enabled' ? (
      <SignUpEmailOnly
        setStep={setStep}
        isSubmittingAccount={isSubmittingAccount}
        setIsSubmittingAccount={setIsSubmittingAccount}
      />
    ) : (
      <SignUp
        setStep={setStep}
        isSubmittingAccount={isSubmittingAccount}
        setIsSubmittingAccount={setIsSubmittingAccount}
      />
    );

  if (!showSignUp) {
    accountText = "Don't have an AppSumo account?";
    headerText = 'Sign in to your account';
    buttonText = 'Sign up';

    loginComponent = (
      <SignIn
        setStep={setStep}
        isSubmittingAccount={isSubmittingAccount}
        setIsSubmittingAccount={setIsSubmittingAccount}
      />
    );
  }

  return (
    <div data-testid="account-checkout-anon-component">
      <Heading.H3 className="mb-3 text-2xl">{headerText}</Heading.H3>
      <div>
        <p className="mb-4">
          You&apos;re oh-so-close to saving on software — you just need to
          create an account so we can email you the goods!
        </p>
        <p className="text-sm">
          {accountText}
          <button
            className="ml-2 text-bolt hover:underline"
            onClick={setShowSignUp}
            data-testid="account-checkout-toggle-existing-user"
          >
            {buttonText}
          </button>
        </p>
        <SocialLogin
          setStep={setStep}
          setStepErrorMessage={setStepErrorMessage}
          isSubmittingAccount={isSubmittingAccount}
          setIsSubmittingAccount={setIsSubmittingAccount}
        />
      </div>

      <div className="mb-4 flex items-center justify-center">
        <div className="mr-3 h-px flex-1 bg-gravel"></div>
        <span className="text-sm font-semibold text-gravel">OR</span>
        <div className="ml-3 h-px flex-1 bg-gravel"></div>
      </div>
      {loginComponent}
    </div>
  );
}

function ExistingAccount({
  user,
  isLoading,
}: Readonly<{
  isLoading: boolean;
  user?: UserObjectProps;
}>): ReactElement {
  if (!user || isLoading) return <></>;

  return (
    <div data-testid="account-checkout-logged-in-component">
      <Heading.H3>Account information</Heading.H3>
      <p className="mt-2">
        <span>
          Order confirmation and redemption instructions will be sent to
        </span>
        <span className="font-bold">&nbsp;{user.email}</span>.
      </p>
    </div>
  );
}

export default function Account(): ReactElement {
  const { step, user, isUserLoading } = useCheckoutState();
  const { setStep, setStepErrorMessage } = useCheckoutAPI();

  const [hasDisplayedMessage, setHasDisplayedMessage] = useState(true);

  // Show green login toaster once logged in
  // A login message via this rout
  useEffect(() => {
    if (isUserLoading) return;

    // User will be null if anonymous
    if (user === null) {
      setHasDisplayedMessage(false);
      return;
    }

    if (!hasDisplayedMessage && user?.username) {
      setHasDisplayedMessage(true);
      toast.success(`Successfully signed in as ${user?.username}.`);
    }
  }, [user, hasDisplayedMessage, isUserLoading]);

  return (
    <CheckoutCard step={STEP.ACCOUNT}>
      <ExistingAccount user={user} isLoading={isUserLoading} />
      <CreateAccount
        user={user}
        setStep={setStep}
        setStepErrorMessage={setStepErrorMessage}
        step={step}
        isLoading={isUserLoading}
      />
      <Loading isLoading={isUserLoading} />
    </CheckoutCard>
  );
}
